import { useMemo } from 'react';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useAppLevelParameters } from './useAppLevelParameters';

/**
 * Resolved `@parameters('name')` values for preview/evaluation, merging the
 * workflow definition's `defaultValue` with the app's `parameters.json`
 * runtime value (the latter wins on collision — that's the runtime override
 * semantics in Logic Apps Standard). Names whose value is `undefined` are
 * omitted so callers can use plain `name in map` checks.
 */
export function useParameterValues(): Record<string, unknown> {
  const appLevel = useAppLevelParameters();
  const workflowDef = useWorkflowStore((s) => s.workflow?.definition?.parameters);

  return useMemo(() => {
    const map: Record<string, unknown> = {};
    if (workflowDef) {
      for (const [name, def] of Object.entries(workflowDef)) {
        const dv = (def as { defaultValue?: unknown } | undefined)?.defaultValue;
        if (dv !== undefined) map[name] = dv;
      }
    }
    if (appLevel) {
      for (const [name, def] of Object.entries(appLevel)) {
        if (def && def.value !== undefined) map[name] = def.value;
      }
    }
    return map;
  }, [appLevel, workflowDef]);
}
